const ATLAS_PROVIDER = 'atlas';

const STATUS_PRESENTATION = {
  pending_issuance: {
    label: 'Preparing voucher',
    className: 'border-amber-200 bg-amber-50 text-amber-800',
  },
  voucher_issued: {
    label: 'Voucher issued',
    className: 'border-blue-200 bg-blue-50 text-blue-800',
  },
  redemption_code_minted: {
    label: 'Sending code',
    className: 'border-blue-200 bg-blue-50 text-blue-800',
  },
  redemption_code_delivered: {
    label: 'Code sent',
    className: 'border-emerald-200 bg-emerald-50 text-emerald-800',
  },
  issuance_failed: {
    label: 'Refunded',
    className: 'border-red-200 bg-red-50 text-red-800',
  },
  active: {
    label: 'Ready to collect',
    className: 'border-emerald-200 bg-emerald-50 text-emerald-800',
  },
  collected: {
    label: 'Collected',
    className: 'border-slate-200 bg-slate-100 text-slate-700',
  },
  expired: {
    label: 'Code expired',
    className: 'border-red-200 bg-red-50 text-red-800',
  },
};

export const isAtlasCashPickup = (collection) => (
  collection?.provider?.toLowerCase() === ATLAS_PROVIDER
);

export const isCashPickupCodeExpired = (collection, now = Date.now()) => {
  const expiryTime = collection?.provider_expiry
    ? Date.parse(collection.provider_expiry)
    : Number.NaN;
  return isAtlasCashPickup(collection)
    && ['redemption_code_minted', 'redemption_code_delivered'].includes(
      collection?.status
    )
    && Number.isFinite(expiryTime)
    && expiryTime <= now;
};

export const getCashPickupStatusPresentation = (collection, now = Date.now()) => {
  const status = isCashPickupCodeExpired(collection, now)
    ? 'expired'
    : collection?.status;
  return STATUS_PRESENTATION[status] || {
    label: status ? status.replaceAll('_', ' ') : 'Processing',
    className: 'border-slate-200 bg-slate-50 text-slate-700',
  };
};

export const getCashPickupActivityLabel = (collection) => {
  if (!isAtlasCashPickup(collection)) {
    return collection?.collected_by_retailer || 'Not collected';
  }
  if (collection?.status === 'redemption_code_delivered') {
    return 'SMS accepted by provider';
  }
  if (collection?.status === 'issuance_failed') {
    return 'Wallet debit refunded';
  }
  return 'Atlas processing';
};

export const isCashPickupTransaction = (transaction) => (
  Boolean(transaction?.cash_pickup_id || transaction?.cash_collection_reference_id)
  && ['send_money', 'cash_pickup_refund'].includes(transaction?.transaction_type)
);

export const isAtlasCashPickupTransaction = (transaction) => (
  isCashPickupTransaction(transaction)
  && transaction?.cash_pickup_provider?.toLowerCase() === ATLAS_PROVIDER
);

export const getCashPickupReferenceId = (transaction) => (
  transaction?.cash_pickup_id || transaction?.cash_collection_reference_id || null
);

export const isCashPickupTransactionCodeExpired = (
  transaction,
  now = Date.now()
) => isCashPickupCodeExpired({
  provider: transaction?.cash_pickup_provider,
  status: transaction?.cash_pickup_status || transaction?.status,
  provider_expiry: transaction?.cash_pickup_expires_at,
}, now);

export const canResendAtlasCashPickupCode = (transaction, now = Date.now()) => (
  transaction?.transaction_type === 'send_money'
  && isAtlasCashPickupTransaction(transaction)
  && Boolean(getCashPickupReferenceId(transaction))
  && isCashPickupTransactionCodeExpired(transaction, now)
);

export const getCashPickupTransactionTitle = (transaction) => {
  const atlas = isAtlasCashPickupTransaction(transaction);
  if (transaction?.transaction_type === 'cash_pickup_refund') {
    return atlas ? 'Atlas Cash Pickup Refund' : 'Cash Pickup Refund';
  }
  const recipient = transaction?.recipient_name || 'caddie';
  return atlas ? `Atlas Cash Pickup to ${recipient}` : `Sent to ${recipient}`;
};

export const getCashPickupTransactionDetail = (transaction) => {
  if (!isAtlasCashPickupTransaction(transaction)) {
    return transaction?.reference_number || transaction?.notes || '';
  }
  if (transaction?.transaction_type === 'cash_pickup_refund') {
    return 'Wallet debit refunded after Atlas voucher issuance failed';
  }
  if (transaction?.status === 'failed' || transaction?.cash_pickup_status === 'issuance_failed') {
    return 'Atlas could not issue the voucher; the wallet debit was refunded';
  }
  if (transaction?.cash_pickup_status === 'redemption_code_delivered') {
    return `Redemption code sent by SMS to ${transaction?.cash_pickup_recipient_phone || 'the caddie'}`;
  }
  return 'Atlas voucher and SMS delivery processing';
};

export const getCashPickupTransactionStatusPresentation = (transaction) => (
  getCashPickupStatusPresentation({
    provider: transaction?.cash_pickup_provider,
    status: transaction?.cash_pickup_status || transaction?.status,
    provider_expiry: transaction?.cash_pickup_expires_at,
  })
);
